import { Component, OnDestroy, OnInit } from '@angular/core';
import { ExportsService } from '@src/app/shared/backend/clients';
import { TranslateService } from '@ngx-translate/core';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { ExportState } from '@src/app/shared/enums/ExportState.enum';
import { FiltersPanelBackendFilter, FiltersPanelChangeEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { ExportTypePipe } from '@app/shared/pipes/exportType.pipe';
import { ExportStatePipe } from '@src/app/shared/pipes/exportState.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-exports',
    templateUrl: './exports.component.html',
    styleUrl: './exports.component.scss',
    host: {
        class: 'app-exports'
    },
    standalone: false
})
export class ExportsComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();
  public readonly exportsTableName = TableSettingService.ExportsTableName;

  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: export type
  private exportTypeOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: export state
  private exportStateOptions: Array<{ id: string | number; text: string }> = [];

  displayDetailsModalbox: boolean = false;
  modalboxDetails: any = null;

  constructor(
    public override tableSettingService: TableSettingService,
    private translateService: TranslateService,
    private toasterService: ToasterService,
    private exportsService: ExportsService) {
      super();
    }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    // i18n propagation on lang change
    this.rebuildExportTypeOptions();
    this.rebuildStateOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildExportTypeOptions();
        this.rebuildStateOptions();
        this.rebuildFiltersPanelOverrides();
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.exportsTableName);

    // default sort for stable pagination + 18n fn labelResolver (sort dropdown options)
    this.initSortPanel({
      defaultSort: { key: 'requestedDate', direction: 'DESC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.exportsService.apiExportsFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.exportsTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.exportsTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    // Important: pass all table cols (not only visible ones) to prevent hidden col filters from affecting results
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const source = evt.source;
    const isApplyAction = source === 'apply';
    const isClearAction = source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  private rebuildExportTypeOptions() {
    const pipe = new ExportTypePipe(this.translateService);
    this.exportTypeOptions = pipe.exportTypesList;
  }

  private rebuildStateOptions() {
    const pipe = new ExportStatePipe(this.translateService);
    this.exportStateOptions = pipe.exportStatesList;
  }

  private rebuildFiltersPanelOverrides(): void {
    this.filtersPanelFieldOverrides = {
      type: {
        fieldLabel: 'TABLE.HEADER.ExportType',
        isMultiselect: true,

        options: this.exportTypeOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      state: {
        fieldLabel: 'TABLE.HEADER.ExportState',
        isMultiselect: true,

        options: this.exportStateOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      }
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    // Update sort labels without resetting the current sort selection
    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        let label;
        if (translated && translated !== translationKey) {
          label = translated;
        } else {
          label = col.header;
        }

        return {
          key: col.field,
          label
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  isDownloadDisabled(status: string): boolean {
    return status !== ExportState.EXPORT_GENERATED && status !== ExportState.EXPORT_DOWNLOADED;
  }

  async downloadExportFile(event: any, exportId: number) {
    this.exportsService.apiExportsDownloadIdGet(exportId)
      .subscribe({
        next: (response: any) => {
          const fileUrl = response.url;

          // Temp <a> to handle download
          const link = document.createElement('a');
          link.href = fileUrl;

          link.download = `export_${exportId}`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        error: (err) => {
          console.error('Error during file download', err);
          this.toasterService.show('error', 'Error', err, 3000);
        }
      });
  }

  openDetails(event: any, exportDetails: object) {
    this.modalboxDetails = exportDetails;
    this.displayDetailsModalbox = true;
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
